/**
 * Every action the permission catalogue declares, in display order. T-07
 * (D-28): this list used to stop at `manage`, so the Roles page silently
 * dropped Submissions.reorder and Submissions.unlock grants. It must equal the
 * distinct actions of the code catalogue (src/lib/rbac/catalogue.ts);
 * tests/rbac/permission-vocabulary.test.ts fails when the two drift.
 */
export const PERMISSION_ACTIONS = ['read', 'write', 'delete', 'sign', 'manage', 'reorder', 'unlock'] as const;
export type Action = (typeof PERMISSION_ACTIONS)[number];

/**
 * phase-roles-and-users-fixes item 3 — plain-language definitions for the
 * permission vocabulary. The matrix governs who may do what in a regulated
 * system; an admin must not have to guess what a column means.
 *
 * `enforced` is an HONEST field, established by reading the route guards.
 * Every action guards real endpoints today. T-07 (D-27) wired `sign`:
 * Documents.sign approves a classification and Submissions.sign approves a
 * submission form. Do not mark an action enforced without checking
 * `requirePermission` usage (src/lib/rbac/route-policy.json lists them).
 */
export const ACTION_DEFINITIONS: Readonly<
  Record<Action, { label: string; definition: string; enforced: boolean }>
> = {
  read: {
    label: 'Read',
    definition:
      'View this kind of record and its details, and run checks, previews and tests that do not change it. For Assistant, Read also covers asking the assistant, which keeps your own conversation history.',
    enforced: true,
  },
  write: {
    label: 'Write',
    definition:
      'Create new records of this kind and edit existing ones. For Users, Write means assign and revoke roles and deactivate or reactivate accounts — only for roles and people whose permissions you hold yourself.',
    enforced: true,
  },
  delete: {
    label: 'Delete',
    definition:
      'Remove records of this kind. Most removals are reversible “hide/withdraw” actions rather than permanent erasure. For Users, Delete means remove someone from the organisation — only someone whose permissions you hold yourself.',
    enforced: true,
  },
  sign: {
    label: 'Sign',
    definition:
      'Sign off records of this kind: approve a document’s classification (Documents) or approve a submission form (Submissions). Write does not include it.',
    enforced: true,
  },
  manage: {
    label: 'Manage',
    definition:
      'Administer the system itself: invite users, create, edit and retire roles, and change org-wide settings and company branding (logo and letterhead). Assigning, revoking and deactivating need Users.write, and removing users needs Users.delete. The strongest permission — by default only the admin role holds it, and no one can grant a permission they do not hold.',
    enforced: true,
  },
  reorder: {
    label: 'Reorder',
    definition: 'Change the order of an application’s sequences. Locked sequences cannot be moved.',
    enforced: true,
  },
  unlock: {
    label: 'Unlock',
    definition: 'Unlock a locked sequence so it can be edited again, and lock it again afterwards.',
    enforced: true,
  },
};

export function isKnownAction(action: string): action is Action {
  return (PERMISSION_ACTIONS as readonly string[]).includes(action);
}

/**
 * The columns of a permission matrix: the catalogue's actions when the
 * catalogue is available, else the vocabulary — plus any action a role
 * actually holds, so no grant is ever hidden. Known actions keep vocabulary
 * order; anything else follows alphabetically.
 */
export function permissionColumns(
  catalogueActions: readonly string[] | null,
  heldActions: Iterable<string> = [],
): string[] {
  const set = new Set<string>(catalogueActions ?? PERMISSION_ACTIONS);
  for (const a of heldActions) set.add(a);
  const known = PERMISSION_ACTIONS.filter((a) => set.has(a));
  const other = [...set].filter((a) => !isKnownAction(a)).sort();
  return [...known, ...other];
}
